import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { UploadCloud, Loader2, FileImage, FileVideo, FileText, Trash2, Eye, CheckCircle2, MessageSquareWarning, Lock, AlertTriangle } from "lucide-react";

const CHUNK_SIZE = 4 * 1024 * 1024;
const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT = "image/*,video/*,application/pdf";

const iconFor = (type) => {
    if ((type || "").startsWith("video/")) return FileVideo;
    if ((type || "").startsWith("image/")) return FileImage;
    return FileText;
};

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const STATUS_STYLE = {
    submitted: "border-amber-400/40 text-amber-300",
    approved: "border-emerald-400/50 text-emerald-300",
    changes_requested: "border-[#FF5A00]/60 text-[#FF5A00]",
    superseded: "border-white/15 text-slate-500",
};
const STATUS_LABEL = {
    submitted: "submitted",
    approved: "approved · locked",
    changes_requested: "changes requested",
    superseded: "superseded",
};

export const ArtworkPanel = ({ campaignId, canUpload = true, staffReview = false }) => {
    const [files, setFiles] = useState(null);
    const [progress, setProgress] = useState(null); // {name, pct}
    const [noteFor, setNoteFor] = useState(null); // file id with open note input
    const [note, setNote] = useState("");
    const [reviewing, setReviewing] = useState(false);

    useEffect(() => {
        api.get(`/artwork/${campaignId}`)
            .then(({ data }) => setFiles(data))
            .catch(() => setFiles([]));
    }, [campaignId]);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > MAX_BYTES) { toast.error("File exceeds the 50 MB limit."); return; }
        const type = file.type || "application/octet-stream";
        if (!(type.startsWith("image/") || type.startsWith("video/") || type === "application/pdf")) {
            toast.error("Only images, videos or PDFs are accepted.");
            return;
        }
        const uploadId = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        setProgress({ name: file.name, pct: 0 });
        try {
            let last = null;
            for (let i = 0; i < total; i++) {
                const fd = new FormData();
                fd.append("upload_id", uploadId);
                fd.append("index", String(i));
                fd.append("total", String(total));
                fd.append("filename", file.name);
                fd.append("content_type", type);
                fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
                const { data } = await api.post(`/artwork/${campaignId}/chunk`, fd);
                last = data;
                setProgress({ name: file.name, pct: Math.round(((i + 1) / total) * 100) });
            }
            if (last?.status === "complete") {
                setFiles((f) => [last.file, ...(f || [])]);
                toast.success("Artwork received — queued for approval.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed — please try again.");
        } finally {
            setProgress(null);
        }
    };

    const view = async (f) => {
        try {
            const { data } = await api.get(`/artwork/file/${f.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            toast.error("Could not open the file.");
        }
    };

    const remove = async (f) => {
        try {
            await api.delete(`/artwork/file/${f.id}`);
            setFiles((list) => (list || []).filter((x) => x.id !== f.id));
            toast.success("Artwork removed.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not remove the file.");
        }
    };

    const review = async (f, action, reviewNote) => {
        setReviewing(true);
        try {
            const { data } = await api.post(`/artwork/file/${f.id}/review`, {
                action,
                note: reviewNote || null,
                origin_url: window.location.origin,
            });
            setFiles((list) => (list || []).map((x) =>
                x.id === f.id ? { ...x, status: data.status, review_note: data.review_note, reviewed_at: data.reviewed_at } : x
            ));
            setNoteFor(null);
            setNote("");
            toast.success(action === "approve"
                ? `Approved${data.email_sent ? " — client notified by email" : ""}.`
                : `Changes requested${data.email_sent ? " — client notified by email" : ""}.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Review failed.");
        } finally {
            setReviewing(false);
        }
    };

    return (
        <div data-testid={`artwork-panel-${campaignId}`}>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">/ Artwork</div>

            {canUpload && (
                <label
                    data-testid="artwork-dropzone"
                    className="block border border-dashed border-white/15 hover:border-[#FF5A00]/50 transition-colors p-5 bg-[#1B2E55]/40 cursor-pointer text-center"
                >
                    <input
                        type="file" accept={ACCEPT} className="hidden"
                        data-testid="artwork-file-input"
                        disabled={!!progress}
                        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
                    />
                    {progress ? (
                        <div>
                            <div className="flex items-center justify-center gap-2 text-xs text-white">
                                <Loader2 size={13} className="animate-spin text-[#FF5A00]" />
                                Uploading {progress.name}… {progress.pct}%
                            </div>
                            <div className="mt-3 h-1 bg-white/10 overflow-hidden max-w-xs mx-auto">
                                <div className="h-full bg-[#FF5A00] transition-all" style={{ width: `${progress.pct}%` }} />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <UploadCloud size={18} className="mx-auto text-[#FF5A00]" />
                            <div className="mt-2 text-xs text-white">Upload your ready-to-run ad</div>
                            <div className="mt-1 text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                                Image · video · PDF — up to 50 MB
                            </div>
                        </div>
                    )}
                </label>
            )}

            {files === null ? (
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 size={11} className="animate-spin" /> Loading artwork…
                </div>
            ) : files.length === 0 ? (
                !canUpload && (
                    <div className="text-xs text-slate-500 border border-white/5 p-3" data-testid="artwork-empty">
                        No artwork submitted yet.
                    </div>
                )
            ) : (
                <ul className="mt-3 space-y-2" data-testid="artwork-file-list">
                    {files.map((f) => {
                        const Icon = iconFor(f.content_type);
                        return (
                            <li key={f.id} className={`border border-white/10 ${f.status === "superseded" ? "opacity-50" : ""}`} data-testid={`artwork-file-${f.id}`}>
                                <div className="px-3 py-2.5 flex items-center gap-3">
                                    <Icon size={15} className="text-[#FF5A00] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white text-xs truncate flex items-center gap-1.5">
                                            {f.original_filename}
                                            <span className="px-1 py-0.5 text-[8px] font-mono-tech uppercase tracking-widest border border-white/15 text-slate-400 shrink-0" data-testid={`artwork-version-${f.id}`}>v{f.version || 1}</span>
                                            {(f.locked || f.status === "approved") && <Lock size={10} className="text-emerald-300 shrink-0" title="Final asset — locked" />}
                                        </div>
                                        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                                            {fmtSize(f.size)} · {new Date(f.uploaded_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                                        </div>
                                    </div>
                                    <span
                                        className={`inline-block px-2 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border shrink-0 ${STATUS_STYLE[f.status] || STATUS_STYLE.submitted}`}
                                        data-testid={`artwork-status-${f.id}`}
                                    >
                                        {STATUS_LABEL[f.status] || f.status}
                                    </span>
                                    <button onClick={() => view(f)} data-testid={`artwork-view-${f.id}`}
                                        className="text-slate-400 hover:text-white transition-colors" title="View">
                                        <Eye size={14} />
                                    </button>
                                    {canUpload && (f.status === "submitted" || f.status === "changes_requested") && !f.locked && (
                                        <button onClick={() => remove(f)} data-testid={`artwork-delete-${f.id}`}
                                            className="text-slate-500 hover:text-[#FF5A00] transition-colors" title="Remove">
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                                {f.validation && !f.validation.passed && f.status !== "superseded" && (
                                    <div className="px-3 pb-2.5 -mt-1 space-y-0.5" data-testid={`artwork-validation-${f.id}`}>
                                        {f.validation.warnings.map((w, i) => (
                                            <div key={i} className="text-[10px] text-amber-300 flex items-start gap-1.5">
                                                <AlertTriangle size={10} className="mt-0.5 shrink-0" /> {w}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {f.review_note && (
                                    <div className="px-3 pb-2.5 -mt-1 text-[11px] text-slate-400 border-l-2 border-[#FF5A00]/50 ml-3 pl-2"
                                        data-testid={`artwork-review-note-${f.id}`}>
                                        {f.review_note}
                                    </div>
                                )}
                                {staffReview && f.status === "submitted" && (
                                    <div className="px-3 pb-3 pt-1">
                                        {noteFor === f.id ? (
                                            <div className="flex items-stretch gap-2">
                                                <input
                                                    type="text" autoFocus maxLength={800}
                                                    value={note}
                                                    onChange={(e) => setNote(e.target.value)}
                                                    placeholder="What needs to change?"
                                                    data-testid={`artwork-changes-note-${f.id}`}
                                                    className="flex-1 bg-[#0F1E3B] border border-white/10 text-white px-2.5 py-1.5 text-xs min-w-0 placeholder:text-slate-600"
                                                />
                                                <button
                                                    disabled={reviewing || !note.trim()}
                                                    onClick={() => review(f, "request_changes", note.trim())}
                                                    data-testid={`artwork-changes-send-${f.id}`}
                                                    className="bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 text-[10px] font-semibold uppercase tracking-widest transition-colors"
                                                >Send</button>
                                                <button onClick={() => { setNoteFor(null); setNote(""); }}
                                                    className="border border-white/15 text-slate-400 hover:text-white px-2 text-[10px] uppercase tracking-widest transition-colors"
                                                >Cancel</button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <button
                                                    disabled={reviewing}
                                                    onClick={() => review(f, "approve", null)}
                                                    data-testid={`artwork-approve-${f.id}`}
                                                    className="inline-flex items-center gap-1.5 border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition-colors disabled:opacity-50"
                                                >
                                                    <CheckCircle2 size={11} /> Approve
                                                </button>
                                                <button
                                                    disabled={reviewing}
                                                    onClick={() => { setNoteFor(f.id); setNote(""); }}
                                                    data-testid={`artwork-request-changes-${f.id}`}
                                                    className="inline-flex items-center gap-1.5 border border-[#FF5A00]/50 text-[#FF5A00] hover:bg-[#FF5A00]/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition-colors disabled:opacity-50"
                                                >
                                                    <MessageSquareWarning size={11} /> Request changes
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};
